import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import AssistantService from '../../services/assistant';
import { fetchCommands, fetchIntegrations } from '../../services/assistant-v2';
import { AssistantResponse } from '../../models/assistant/interfaces/AssistantResponse';
import { Inbox } from '../../models/assistant/interfaces/subject/Inbox';
import { Service } from '../../models/assistant/interfaces/subject/Service';

/**
 * Query keys for assistant data
 */
export const ASSISTANT_INBOX_QUERY_KEY = ['assistant', 'inbox'];
export const ASSISTANT_INBOX_COUNT_QUERY_KEY = ['assistant', 'inboxCount'];
export const ASSISTANT_INTEGRATIONS_QUERY_KEY = ['assistant', 'integrations'];
export const ASSISTANT_COMMANDS_QUERY_KEY = ['assistant', 'commands'];

/**
 * Threshold for considering data as old (1 hour)
 */
const OLD_DATA_THRESHOLD = 1000 * 60 * 60; // 1 hour

export interface InboxCountResponse {
  count: number;
}

/**
 * Hook to fetch inbox data from the assistant service
 *
 * @param programId - Target program identifier
 * @param advocateId - Optional advocate identifier for filtering results
 * @param options - Additional react-query options for configuring caching,
 *                  staleness, and refetch behavior
 * @returns Standard react-query object containing inbox data and query state
 */
export const useAssistantInboxQuery = (
  programId: string,
  advocateId?: number,
  options?: Omit<
    UseQueryOptions<
      Array<AssistantResponse<Inbox>>,
      Error,
      Array<AssistantResponse<Inbox>>,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    Array<AssistantResponse<Inbox>>,
    Error,
    Array<AssistantResponse<Inbox>>,
    string[]
  >({
    queryKey: [
      ...ASSISTANT_INBOX_QUERY_KEY,
      programId,
      advocateId?.toString() || '',
    ],
    queryFn: async () => {
      const service = new AssistantService(programId, advocateId);
      return await service.fetchInbox();
    },
    enabled: !!programId,
    ...options,
  });
};

/**
 * Hook to fetch the count of inbox items
 *
 * @param programId - Target program identifier
 * @param advocateId - Optional advocate identifier for filtering results
 * @param options - Additional react-query options to configure the query behavior
 * @returns Query result containing the inbox count and standard query state flags
 */
export const useAssistantInboxCountQuery = (
  programId: string,
  advocateId?: number,
  options?: Omit<
    UseQueryOptions<InboxCountResponse, Error, InboxCountResponse, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<InboxCountResponse, Error, InboxCountResponse, string[]>({
    queryKey: [
      ...ASSISTANT_INBOX_COUNT_QUERY_KEY,
      programId,
      advocateId?.toString() || '',
    ],
    queryFn: async () => {
      const service = new AssistantService(programId, advocateId);
      const response = await service.fetchInboxCount();
      return response.data;
    },
    enabled: !!programId,
    ...options,
  });
};

/**
 * Hook to fetch available service integrations
 *
 * @param options - React-query configuration options
 * @returns Enhanced query result object that includes:
 *   - data: The integrations data when available
 *   - isOldData: Boolean flag indicating if data exceeds freshness threshold
 *   - Standard react-query states (isLoading, isError, etc.)
 */
export const useIntegrationsQuery = (
  options?: Omit<
    UseQueryOptions<
      Array<AssistantResponse<Service>>,
      Error,
      Array<AssistantResponse<Service>>,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
): {
  data: Array<AssistantResponse<Service>> | undefined;
  isOldData: boolean;
  isLoading: boolean;
  isError: boolean;
  isRefetching: boolean;
  isFetching: boolean;
} => {
  const query = useQuery<
    Array<AssistantResponse<Service>>,
    Error,
    Array<AssistantResponse<Service>>,
    string[]
  >({
    queryKey: ASSISTANT_INTEGRATIONS_QUERY_KEY,
    queryFn: async () => fetchIntegrations(),
    keepPreviousData: true,
    ...options,
  });

  // Data is old if there is data and it is older than 1 hour ago
  const isOldData =
    !!query.data &&
    !!query.dataUpdatedAt &&
    query.dataUpdatedAt < Date.now() - OLD_DATA_THRESHOLD;

  return {
    data: query.data,
    isOldData,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
  };
};

/**
 * Hook to fetch available assistant commands
 *
 * @returns Standard react-query result object containing:
 *   - data: Array of available commands
 *   - Query state flags (isLoading, isError, etc.)
 */
export const useCommandsQuery = () =>
  useQuery({
    queryKey: ASSISTANT_COMMANDS_QUERY_KEY,
    queryFn: () => fetchCommands(),
  });
